<template>
  <header class="vdb-header">
    <div class="vdb-header-inner">
      <div class="vdb-header-left">
        <router-link to="/" class="vdb-logo" humblytics="nav-logo">
          <span class="vdb-logo-mark">VD</span>
          <span class="vdb-logo-name">VideoDatabase</span>
        </router-link>
        <nav class="vdb-nav">
          <router-link
            v-for="tab in visibleTabs"
            :key="tab.key"
            :to="tab.href"
            :humblytics="'nav-' + tab.key"
            :class="{ active: isActive(tab.key) }"
            class="nav-item"
          >{{ tab.label }}</router-link>
          <router-link to="/blog" class="nav-item nav-item-secondary" :class="{ active: isActive('blog') }" humblytics="nav-blog">Blog</router-link>
          <div v-if="isAuthenticated" class="nav-item-dropdown" @mouseleave="betaOpen = false">
            <button class="nav-item" :class="{ active: isActive('beta') }" humblytics="nav-beta-toggle" @click="betaOpen = !betaOpen" @mouseenter="betaOpen = true">Beta ▾</button>
            <div v-if="betaOpen" class="vdb-admin-dropdown" @click="betaOpen = false">
              <div class="vdb-admin-group-label">New test features</div>
              <router-link v-for="item in betaItems" :key="item.key" :to="item.href" :humblytics="'nav-beta-' + item.key">{{ item.label }}</router-link>
            </div>
          </div>
          <div v-if="isAdmin" class="nav-item-dropdown" @mouseleave="adminOpen = false">
            <button class="nav-item" :class="{ active: isActive('admin') }" humblytics="nav-admin-toggle" @click="adminOpen = !adminOpen" @mouseenter="adminOpen = true">Admin ▾</button>
            <div v-if="adminOpen" class="vdb-admin-dropdown" @click="adminOpen = false">
              <div class="vdb-admin-group-label">Database</div>
              <router-link to="/admin/creators">Creators</router-link>
              <router-link to="/admin/topics">Topics</router-link>
              <router-link to="/admin/concepts">Concepts</router-link>
              <router-link to="/admin/formats">Formats</router-link>
              <router-link to="/admin/elements">Elements</router-link>
              <router-link to="/admin/account-types">Account Types</router-link>
              <router-link to="/admin/taxonomy-examples">Curated Content</router-link>
              <div class="vdb-admin-group-label">Users</div>
              <router-link to="/admin/users">Users</router-link>
              <router-link to="/admin/invites">Invites</router-link>
              <router-link to="/admin/tool-usage">MCP Usage</router-link>
              <div class="vdb-admin-group-label">Content</div>
              <router-link to="/bot-admin">Bot</router-link>
              <router-link to="/admin/content-generation">Content Gen</router-link>
              <router-link to="/admin/blog">Editorial Blog</router-link>
              <router-link to="/admin/cut30-newsletter">Cut30 Newsletter</router-link>
              <router-link to="/admin/strategy-audit">Strategy Audit</router-link>
              <router-link to="/admin/embeds">Embeds</router-link>
              <div class="vdb-admin-group-label">Competitors</div>
              <router-link to="/admin/competitors/accounts">Accounts</router-link>
              <router-link to="/admin/competitors/videos">Videos</router-link>
              <router-link to="/admin/competitors/posts">Posts</router-link>
              <div class="vdb-admin-group-label">Settings</div>
              <router-link to="/admin/search-settings">Search Settings</router-link>
              <router-link to="/admin/search-logs">Search Logs</router-link>
              <router-link to="/admin/creator-panel">Creator Panel</router-link>
              <router-link to="/admin/verification">Verification</router-link>
              <router-link to="/views">Views</router-link>
              <router-link to="/admin/email-submissions">Email Submissions</router-link>
            </div>
          </div>
        </nav>
      </div>
      <div class="vdb-header-right">
        <div class="vdb-header-search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--ink-400)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>
          </svg>
          <input
            v-model="headerSearch"
            type="text"
            placeholder="Search videos..."
            humblytics="nav-search"
            @keyup.enter="submitSearch"
            @focus="onSearchFocus"
          />
          <kbd class="vdb-kbd">&#8984;K</kbd>
        </div>
        <template v-if="isAuthenticated">
          <div class="vdb-ava-wrap" humblytics="nav-profile-avatar" @click="profileOpen = !profileOpen" v-click-outside="closeProfile">
            <div class="vdb-ava" :style="{ background: avatarColor }">{{ userInitials }}</div>
            <div v-if="profileOpen" class="vdb-profile-dropdown" @click.stop>
              <div class="vdb-profile-email">{{ userEmail }}</div>
              <div class="vdb-profile-sep"></div>
              <router-link to="/profile" class="vdb-profile-item" humblytics="nav-profile-link" @click="profileOpen = false">Profile</router-link>
              <router-link to="/subscription" class="vdb-profile-item" humblytics="nav-subscription-link" @click="profileOpen = false">Subscription</router-link>
              <router-link to="/swipefiles" class="vdb-profile-item" humblytics="nav-swipefiles-link" @click="profileOpen = false">Swipefiles</router-link>
              <div class="vdb-profile-sep"></div>
              <button class="vdb-profile-item vdb-profile-signout" humblytics="nav-signout" @click="handleSignOut">Sign out</button>
            </div>
          </div>
        </template>
        <template v-else>
          <router-link to="/login" class="vdb-btn-pill ghost" humblytics="nav-signin">Sign in</router-link>
          <router-link to="/signup" class="vdb-btn-pill" humblytics="nav-get-started">Get started</router-link>
        </template>
      </div>

      <!-- Mobile nav toggle -->
      <button
        class="vdb-mobile-toggle"
        humblytics="nav-mobile-toggle"
        :aria-label="mobileOpen ? 'Close navigation menu' : 'Open navigation menu'"
        :aria-expanded="mobileOpen"
        aria-controls="vdb-mobile-navigation"
        @click="mobileOpen = !mobileOpen"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 12h18M3 6h18M3 18h18"/>
        </svg>
      </button>
    </div>

    <!-- Mobile nav overlay -->
    <div v-if="mobileOpen" class="vdb-mobile-overlay" @click="mobileOpen = false"></div>
    <!-- Mobile nav drawer -->
    <div v-if="mobileOpen" id="vdb-mobile-navigation" class="vdb-mobile-nav">
      <!-- Mobile search -->
      <div class="vdb-mobile-search">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--ink-400)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>
        </svg>
        <input
          v-model="headerSearch"
          type="text"
          placeholder="Search videos..."
          @keyup.enter="submitSearch(); mobileOpen = false"
        />
      </div>
      <!-- Add to Queue button -->
      <router-link to="/submit-video" class="vdb-mobile-nav-item" humblytics="nav-mobile-add-queue" style="display: flex; align-items: center; gap: 8px; color: var(--accent-text); font-weight: 600;" @click="mobileOpen = false">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
          <path d="M12 5v14M5 12h14"/>
        </svg>
        Add to Queue
      </router-link>
      <hr class="vdb-dash" style="margin: 8px 0"/>
      <router-link
        v-for="tab in allTabs"
        :key="'m-' + tab.key"
        :to="tab.href"
        :humblytics="'nav-mobile-' + tab.key"
        class="vdb-mobile-nav-item"
        :class="{ active: isActive(tab.key) }"
        @click="mobileOpen = false"
      >{{ tab.label }}</router-link>
      <hr class="vdb-dash" style="margin: 8px 0"/>
      <template v-if="!isAuthenticated">
        <router-link to="/login" class="vdb-mobile-nav-item" humblytics="nav-mobile-signin" @click="mobileOpen = false">Sign in</router-link>
        <router-link to="/signup" class="vdb-mobile-nav-item" humblytics="nav-mobile-get-started" @click="mobileOpen = false">Get started</router-link>
      </template>
      <template v-else>
        <div class="vdb-mobile-nav-label">Beta</div>
        <router-link v-for="item in betaItems" :key="'m-beta-' + item.key" :to="item.href" class="vdb-mobile-nav-item" :humblytics="'nav-mobile-beta-' + item.key" @click="mobileOpen = false">{{ item.label }}</router-link>
        <hr class="vdb-dash" style="margin: 8px 0"/>
        <router-link to="/profile" class="vdb-mobile-nav-item" humblytics="nav-mobile-profile" @click="mobileOpen = false">Profile</router-link>
        <router-link to="/swipefiles" class="vdb-mobile-nav-item" humblytics="nav-mobile-swipefiles" @click="mobileOpen = false">Swipefiles</router-link>
      </template>
    </div>
  </header>
</template>

<script>
import { authManager } from '@/services/auth.js';
import tierService from '@/services/tierService.js';

export default {
  name: 'VdbHeader',
  directives: {
    'click-outside': {
      mounted(el, binding) {
        el._clickOutside = (e) => { if (!el.contains(e.target)) binding.value(); };
        document.addEventListener('click', el._clickOutside);
      },
      unmounted(el) {
        document.removeEventListener('click', el._clickOutside);
      }
    }
  },
  data() {
    return { mobileOpen: false, headerSearch: '', adminOpen: false, betaOpen: false, profileOpen: false, userTier: null };
  },
  computed: {
    isAuthenticated() {
      return authManager.isAuthenticated;
    },
    isAdmin() {
      return this.userTier?.tier === 'admin';
    },
    userInitials() {
      const user = authManager.user;
      if (!user) return 'A';
      const name = user.user_metadata?.full_name || user.email || '';
      return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'A';
    },
    avatarColor() {
      return 'var(--accent)';
    },
    userEmail() {
      return authManager.user?.email || '';
    },
    // Beta menu: new test features for signed-in users. Each page shows a BetaBanner with a feedback button.
    betaItems() {
      return [
        { key: 'hooker', label: 'Hooker', href: '/hooker' },
        { key: 'ideas', label: 'Idea Room', href: '/ideas' },
        { key: 'boards', label: 'Boards', href: '/boards' },
      ];
    },
    visibleTabs() {
      const tabs = [
        { key: 'videos', label: 'Videos', href: '/videos' },
        { key: 'topics', label: 'Topics', href: '/topics' },
        { key: 'creators', label: 'Creators', href: '/creators' },
        { key: 'hooks', label: 'Hooks', href: '/hooks' },
        { key: 'formats', label: 'Formats', href: '/formats' },
        { key: 'concepts', label: 'Concepts', href: '/concepts' },
      ];
      if (this.isAuthenticated) {
        tabs.push({ key: 'feed', label: 'Feed', href: '/feed' });
      }
      return tabs;
    },
    allTabs() {
      return [
        ...this.visibleTabs,
        { key: 'elements', label: 'Elements', href: '/elements' },
        { key: 'blog', label: 'Blog', href: '/blog' },
      ];
    },
  },
  watch: {
    '$route'() {
      this.mobileOpen = false;
    },
  },
  async mounted() {
    if (authManager.isAuthenticated && authManager.user?.id) {
      try {
        this.userTier = await tierService.getUserTier(authManager.user.id);
      } catch {}
    }
  },
  methods: {
    submitSearch() {
      const q = this.headerSearch.trim();
      if (q) {
        this.$router.push({ path: '/videos', query: { search: q } });
        this.headerSearch = '';
      }
    },
    onSearchFocus() {
      if (this.$route.path !== '/videos') {
        this.$emit('search');
      }
    },
    closeProfile() {
      this.profileOpen = false;
    },
    async handleSignOut() {
      this.profileOpen = false;
      await authManager.signOut();
      this.$router.push('/');
    },
    isActive(key) {
      const path = this.$route.path;
      if (key === 'home') return path === '/' || path === '/home';
      if (key === 'videos') return path === '/videos' || path.startsWith('/video/');
      if (key === 'hooks') return path.startsWith('/hook') && !path.startsWith('/hooker');
      if (key === 'beta') return path.startsWith('/hooker') || path.startsWith('/ideas') || path.startsWith('/boards') || /^\/swipefiles\/[^/]+\/board/.test(path);
      return path.startsWith('/' + key);
    },
  },
};
</script>

<style scoped>
.vdb-mobile-nav-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-500);
  padding: 6px 12px 2px;
}
.vdb-header-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border: 1px solid var(--line-200);
  border-radius: 100px;
  background: var(--paper);
  cursor: text;
}
.vdb-header-search input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: var(--ink-700);
  width: 140px;
  font-family: inherit;
}
.vdb-header-search input::placeholder {
  color: var(--ink-400);
}
.vdb-kbd {
  margin-left: 14px;
  padding: 2px 6px;
  font-size: 11px;
  color: var(--ink-400);
  border: 1px solid var(--line-200);
  border-radius: 4px;
  background: rgba(0,0,0,0.02);
  font-family: inherit;
}
.vdb-mobile-toggle {
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--ink-700);
  padding: 8px;
}
.vdb-mobile-overlay {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.3);
  z-index: 99;
}
.vdb-mobile-nav {
  display: none;
  position: absolute;
  top: 100%;
  left: 0; right: 0;
  background: white;
  padding: 16px;
  border-bottom: 1px solid var(--line-200);
  box-shadow: var(--shadow-card-md);
  flex-direction: column;
  gap: 4px;
  z-index: 100;
  max-height: calc(100vh - 60px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.vdb-mobile-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--cream-100);
  border: 1px solid var(--line-200);
  border-radius: 10px;
  margin-bottom: 8px;
}
.vdb-mobile-search input {
  flex: 1;
  border: none;
  background: none;
  font-size: 15px;
  font-family: var(--font-sans);
  color: var(--ink-700);
  outline: none;
}
.vdb-mobile-search input::placeholder {
  color: var(--ink-300);
}
.vdb-mobile-nav-item {
  display: block;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  color: var(--ink-700);
  text-decoration: none;
}
.vdb-mobile-nav-item:hover { background: var(--cream-100); }
.vdb-mobile-nav-item.active { background: var(--cream-200); color: var(--ink-800); font-weight: 600; }
.nav-item-dropdown {
  position: relative;
}
.nav-item-secondary {
  border-left: 1px solid var(--line-200);
  margin-left: 2px;
  padding-left: 14px;
}
.nav-item-dropdown > button {
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
}
.vdb-admin-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: var(--paper);
  border: 1px solid var(--line-200);
  border-radius: 10px;
  box-shadow: var(--shadow-card-md);
  padding: 6px;
  min-width: 180px;
  z-index: 200;
  display: flex;
  flex-direction: column;
}
.vdb-admin-dropdown::before {
  content: '';
  position: absolute;
  top: -12px;
  left: 0;
  right: 0;
  height: 12px;
}
.vdb-admin-dropdown a {
  display: block;
  padding: 7px 12px;
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-700);
  text-decoration: none;
  border-radius: 6px;
}
.vdb-admin-dropdown a:hover {
  background: var(--cream-100);
}
.vdb-admin-group-label {
  padding: 8px 12px 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-400);
}
.vdb-admin-group-label:not(:first-child) {
  border-top: 1px solid var(--line-200);
  margin-top: 4px;
  padding-top: 10px;
}
.vdb-ava-wrap {
  position: relative;
  cursor: pointer;
}
.vdb-profile-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: var(--paper);
  border: 1px solid var(--line-200);
  border-radius: 10px;
  box-shadow: var(--shadow-card-md);
  padding: 6px;
  min-width: 200px;
  z-index: 200;
  display: flex;
  flex-direction: column;
}
.vdb-profile-email {
  padding: 8px 12px;
  font-size: 12px;
  color: var(--ink-400);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.vdb-profile-sep {
  height: 1px;
  background: var(--line-200);
  margin: 4px 0;
}
.vdb-profile-item {
  display: block;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-700);
  text-decoration: none;
  border-radius: 6px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  width: 100%;
}
.vdb-profile-item:hover {
  background: var(--cream-100);
}
.vdb-profile-signout {
  color: var(--ink-400);
}
@media (max-width: 768px) {
  .vdb-mobile-toggle { display: block; }
  .vdb-mobile-overlay { display: block; }
  .vdb-mobile-nav { display: flex; }
  .vdb-header-right { display: none; }
}
</style>
